import React, { useState, useEffect } from 'react';
import { X, Smartphone, ArrowRight, Apple, Chrome } from 'lucide-react';
import {
  shouldShowMobileRedirect,
  dismissMobileBanner,
  getAppStoreUrl,
  getPlatform,
  openMobileApp,
  isIOS,
  isAndroid,
} from '../utils/mobileDetection';

/**
 * MobileAppModal - Shows a full-screen modal on first visit
 * prompting mobile users to use the mobile app
 */
const MobileAppModal = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [platform, setPlatform] = useState('desktop');

  useEffect(() => {
    // Show modal after a short delay for better UX
    const timer = setTimeout(() => {
      if (shouldShowMobileRedirect()) {
        setIsVisible(true);
        setPlatform(getPlatform());
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    dismissMobileBanner();
    setIsVisible(false);
  };

  const handleOpenApp = () => {
    const currentPath = window.location.pathname;
    openMobileApp(currentPath);
  };

  const handleContinueInBrowser = () => {
    dismissMobileBanner();
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors z-10"
          aria-label="Close modal"
        >
          <X className="h-6 w-6" />
        </button>

        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-3xl shadow-lg mb-4">
            <Smartphone className="h-10 w-10 text-orange-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Use Lodgeic Mobile App
          </h2>
          <p className="text-orange-100 text-sm">
            Get the best experience with our native {platform === 'ios' ? 'iOS' : 'Android'} app
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Features */}
          <div className="space-y-3 mb-6">
            <Feature text="Faster performance" />
            <Feature text="Optimized for mobile" />
          </div>

          {/* Actions */}
          <div className="space-y-3">
            {/* Primary action - Open/Download app */}
            <button
              onClick={handleOpenApp}
              className="w-full py-4 px-6 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors flex items-center justify-center space-x-2 shadow-md"
            >
              {isIOS() && <Apple className="h-5 w-5" />}
              {isAndroid() && <Chrome className="h-5 w-5" />}
              <span>Open in App</span>
              <ArrowRight className="h-5 w-5" />
            </button>

            {/* Secondary action - Continue in browser */}
            <button
              onClick={handleContinueInBrowser}
              className="w-full py-3 px-6 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              Continue in Browser
            </button>
          </div>

          {/* Footer note */}
          <p className="text-xs text-gray-500 text-center mt-4">
            Don't have the app? We'll redirect you to the{' '}
            {platform === 'ios' ? 'App Store' : 'Play Store'}
          </p>
        </div>
      </div>
    </div>
  );
};

const Feature = ({ text }) => (
  <div className="flex items-center space-x-3">
    <div className="flex-shrink-0 w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center">
      <svg
        className="w-4 h-4 text-orange-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 13l4 4L19 7"
        />
      </svg>
    </div>
    <span className="text-gray-700">{text}</span>
  </div>
);

export default MobileAppModal;
